// Store Display Group Planogram Apply main entity
export interface StoreDisplayGroupPlanogramApply {
  id: number;                          // 主键
  source: string;                     // 来源 benchmark\ algorithm
  applyNo: string;                     // 申请单号
  planogramCode: string;               // planogram code
  planogramImageUrl?: string;          // 陈列图图片URL
  storeCode: string;                   // 门店Code
  storeName: string;                   // 门店Name
  storeGroupCode: string;              // 门店组Code
  storeGroupName: string;              // 门店组名称
  displayGroupCode: string;            // 陈列分组code
  displayGroupName: string;            // 陈列分组名称
  teamType: TeamType;                  // 团队类型：COMMERCIAL - 采购，PLANOGRAM - 制图
  applyStatus: ApplyStatus;            // 申请状态：PENDING-待审核，APPROVED-已通过，REJECTED-已拒绝
  applyPerson: string;                 // 申请人
  applyPersonName: string;             // 申请人姓名
  applyTime: string;                   // 申请时间
  applyReason: string;                 // 申请原因
  auditPerson?: string;                // 审批人
  auditPersonName?: string;            // 审批人姓名
  auditTime?: string;                  // 审批时间
  auditRemark?: string;                // 审批备注说明
  expectedEndDate?: string;            // 预期结束执行日期
  actualEndDate?: string;              // 实际结束执行日期
  creatorAccount: string;              // 创建人账号
  creatorName: string;                 // 创建人姓名
  gmtCreate: string;                   // 创建时间
  modifierAccount: string;             // 修改人账号
  modifierName: string;                // 修改人姓名
  gmtModified: string;                 // 修改时间
  deleted: number;                     // 逻辑删除标记
  mainDivisionCode?: string;           // 事业部编码
  mainDivisionName?: string;           // 事业部名称
}

// Team type enum
export enum TeamType {
  COMMERCIAL = 'COMMERCIAL',           // 采购
  PLANOGRAM = 'PLANOGRAM',              // 制图
  OPERATE = 'OPERATE',
}

// Apply status enum
export enum ApplyStatus {
  PENDING = 'PENDING',                 // 待审核
  APPROVED = 'APPROVED',               // 已通过
  REJECTED = 'REJECTED'                // 已拒绝
}

// Filter criteria for search
export interface StoreDisplayGroupPlanogramApplyFilters {
  source?: string;                      // 来源 benchmark\ algorithm  
  applyNo?: string;                    // 申请单号
  storeGroupCode?: string;             // 门店组编码
  storeGroupName?: string;             // 门店组名称
  displayGroupCode?: string;           // 陈列分组编码
  teamType?: TeamType;                 // 团队类型
  applyStatus?: ApplyStatus;           // 申请状态
  applyPerson?: string;                // 申请人
  startDate?: string;                  // 申请开始日期
  endDate?: string;                    // 申请结束日期
  search?: string;                     // 全文搜索
  mainDivisionCode?: string;           // 事业部编码  // 添加这一行
}

// Create apply request
export interface CreateStoreDisplayGroupPlanogramApplyRequest {
  planogramCode: string;               // planogram code
  storeCode: string;                   // 门店Code
  storeName: string;                   // 门店Name
  displayGroupCode: string;            // 陈列分组code
  displayGroupName: string;            // 陈列分组名称
  teamType: TeamType;                  // 团队类型
  applyReason: string;                 // 申请原因
  expectedEndDate?: string;            // 预期结束执行日期
}

// Update apply request
export interface UpdateStoreDisplayGroupPlanogramApplyRequest {
  id: number;                          // 主键
  applyReason?: string;                // 申请原因
  expectedEndDate?: string;            // 预期结束执行日期
}

// Audit apply request
export interface AuditStoreDisplayGroupPlanogramApplyRequest {
  id: number;                          // 主键
  applyStatus: ApplyStatus.APPROVED | ApplyStatus.REJECTED; // 审批状态
  auditRemark?: string;                // 审批备注说明
}

// Pagination parameters
export interface PaginationParams {
  page: number;
  pageSize: number;
  sortBy?: SortField;
  sortOrder?: 'asc' | 'desc';
}

// API Response wrapper following backend standards
export interface ApiResponse<T> {
  traceId: string;
  success: boolean;
  msg?: string | null;
  errorCode?: string | null;
  errorMessage: string | null;
  flag: 'success' | 'fail';
  code?: string | null;
  message?: string | null;
  data: T | null;
}

// Paginated response for lists following backend standards
export interface PaginatedResponse<T> {
  tableData: T[];
  paging: {
    currentPage: number;
    totalCount: number;
    pageSize: number;
  };
}

// Sort options for table
export type SortField = 
  | 'id'
  | 'applyNo'
  | 'storeGroupCode'
  | 'storeGroupName'
  | 'displayGroupCode'
  | 'teamType'
  | 'applyStatus'
  | 'applyTime'
  | 'auditTime'
  | 'expectedEndDate'
  | 'actualEndDate'
  | 'gmtCreate'
  | 'gmtModified';

export type SortOrder = 'asc' | 'desc';

// Table column keys
export type TableColumnKey = 
  | 'applyNo'
  | 'storeGroupCode'
  | 'storeGroupName'
  | 'displayGroupCode'
  | 'displayGroupName'
  | 'teamType'
  | 'applyStatus'
  | 'applyPerson'
  | 'applyTime'
  | 'auditPerson'
  | 'auditTime'
  | 'expectedEndDate'
  | 'actions';

// Error types for better error handling
export interface ErrorInfo {
  type: 'network' | 'server' | 'permission' | 'notFound' | 'validation';
  message: string;
  code?: string;
  traceId?: string;
}

// Loading states
export interface LoadingState {
  list: boolean;
  detail: boolean;
  create: boolean;
  update: boolean;
  audit: boolean;
  storeCodes: boolean;
  planogramCodes: boolean;
  displayGroupCodes: boolean;
}

// Component props interfaces
export interface FilterPanelProps {
  filters: StoreDisplayGroupPlanogramApplyFilters;
  onFiltersChange: (filters: StoreDisplayGroupPlanogramApplyFilters) => void;
  onSearch: () => void;
  onReset: () => void;
  loading?: boolean;
  storeGroupCodes: Array<{ code: string; name: string }>;
  displayGroupCodes: Array<{ code: string; name: string }>;
}

export interface CreateApplyFormProps {
  onSubmit: (values: CreateStoreDisplayGroupPlanogramApplyRequest) => void;
  onCancel: () => void;
  loading?: boolean;
  storeCodes: Array<{ code: string; name: string }>;
  planogramCodes: Array<{ code: string; name: string }>;
  displayGroupCodes: Array<{ code: string; name: string }>;
}

export interface AuditApplyFormProps {
  apply: StoreDisplayGroupPlanogramApply;
  onSubmit: (values: AuditStoreDisplayGroupPlanogramApplyRequest) => void;
  onCancel: () => void;
  loading?: boolean;
}

export interface StoreDisplayGroupPlanogramApplyListProps {
  // No props needed - self-contained component
}

export interface StoreDisplayGroupPlanogramApplyDetailProps {
  id: number;
}

// Status color mapping for UI display
export const APPLY_STATUS_COLORS = {
  [ApplyStatus.PENDING]: 'orange',
  [ApplyStatus.APPROVED]: 'green',
  [ApplyStatus.REJECTED]: 'red',
} as const;

export const TEAM_TYPE_COLORS = {
  [TeamType.COMMERCIAL]: 'blue',
  [TeamType.PLANOGRAM]: 'purple',
} as const;

// Form validation rules
export interface ValidationRules {
  applyReason: {
    required: true;
    maxLength: number;
  };
  expectedEndDate: {
    required: false;
  };
  auditRemark: {
    maxLength: number;
  };
}

export const VALIDATION_RULES: ValidationRules = {
  applyReason: {
    required: true,
    maxLength: 500,
  },
  expectedEndDate: {
    required: false,
  },
  auditRemark: {
    maxLength: 500,
  },
};